import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Typography,
} from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { ShopperOrder, Streams, StreamShopper } from 'api';
import moment from 'moment';
import React, { Fragment, useState } from 'react';
import { useHistory } from 'react-router';
import { CustomSVGSeries, Hint, LineSeries, XAxis, XYPlot, YAxis } from 'react-vis';
import 'react-vis/dist/style.css';
import './rv-overide.css';

interface MallRowItem {
  meta: ShopperOrder;
  x: number;
  y: number;
  size: number;
}

const CAPACITY_THRESHOLDS = {
  HIGH: 60,
  MEDIUM: 40,
} as const;

const CAPACITY_STYLES = {
  HIGH: {
    background: '#57A946',
    color: '#fff',
    indicator: '#fff',
  },
  MEDIUM: {
    background: '#FEEDB5',
    color: '#000',
    indicator: '#FFB800',
  },
  LOW: {
    background: '#F7D8D8',
    color: '#A50E0E',
    indicator: '#A50E0E',
  },
} as const;

const getCapacityLevel = (capacity: number): keyof typeof CAPACITY_STYLES => {
  if (capacity > CAPACITY_THRESHOLDS.HIGH) return 'HIGH';
  if (capacity > CAPACITY_THRESHOLDS.MEDIUM) return 'MEDIUM';
  return 'LOW';
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingBlock: '1.69rem',
      width: '100%',
    },
    header: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '2rem',
    },
    legend: {
      paddingBlock: '1.8rem',
      borderTop: '1px solid #F4F4F4',
      display: 'flex',
      justifyContent: 'center',
      gap: '1.5rem',
    },
    indicator: {
      width: '1.25rem',
      height: '1.25rem',
      background: '#75CCF2',
      borderRadius: '50%',
    },
    footerText: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      gap: '0.2rem',
      color: '#000',
      fontSize: '0.875rem',
    },
    viewDetails: {
      fontSize: '0.875rem',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    legendText: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
    },
    expanded: {
      borderTop: '1px solid #F4F4F4',
      '&::before': {
        borderTop: '1px solid #F4F4F4',
        backgroundColor: 'transparent',
      },
    },
    cross: {
      display: 'inline-flex',
      padding: '1.5rem',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1.2rem',
      borderRadius: '0.5rem',
      background: '#fff',
      zIndex: 1000,
      position: 'relative',
      boxShadow: '0px 0px 4px 0px rgb(0 0 0 / 6%)',
    },
    crossInner: {
      minWidth: '18rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      color: '#000',
      fontSize: '0.875rem',
      fontFamily: 'Roboto',
    },
    crossText: {
      fontWeight: 300,
      width: '100%',
      textAlign: 'right',
    },
    bolder: {
      fontWeight: 500,
      textWrap: 'nowrap',
    },
    crossImgWrapper: {
      display: 'flex',
      gap: '0.5rem',
      width: '100%',
      alignItems: 'center',
      '& p': {
        width: '100%',
      },
    },
    crossImg: {
      width: '1.5rem',
      height: '1.5rem',
      borderRadius: '50%',
    },
    mallTitleWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      position: 'relative',
      minWidth: '10rem',
      maxWidth: '10rem',
      '@media (min-width: 960px)': {
        minWidth: '14rem',
        maxWidth: '14rem',
      },
    },
    missingTiming: {
      position: 'absolute',
      bottom: '-1rem',
      left: 0,
      color: '#A50E0E',
    },
    capacityChip: {
      borderRadius: '20px',
      fontSize: '12px',
      height: '22px',
      minWidth: '10rem',
      '& .MuiChip-label': {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      },
    },
    capacityIndicator: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
  })
);

function getColor(order: ShopperOrder, shopper: StreamShopper) {
  const shopperStatus = shopper.shopperStatus?.toLowerCase();
  const orderStatus = order.shoppingStatus?.toLowerCase();

  if (orderStatus === 'late' || order.runningLate || order.badTiming) {
    return '#D32F2F';
  }

  if (shopperStatus !== 'active') {
    return '#999999';
  }

  if (['progress', 'completed'].includes(orderStatus)) {
    return '#5BB980';
  }

  if (orderStatus === 'scheduled') {
    return '#263B84';
  }

  return '#CCCCCC';
}
function generateAxesValues(mall: Streams, earliestStoreStartTime: number, latestStoreEndTime: number) {
  const ordersByShopper = new Map<number, ShopperOrder[]>();
  mall.shoppers.forEach((shopper) => {
    ordersByShopper.set(shopper.shopperID, []);
  });

  mall.shoppers.forEach((shopper) => {
    shopper.orders.forEach((order) => {
      // if (order.activeShopperID) {
      //   const activeShopperOrders = ordersByShopper.get(order.activeShopperID) || [];
      //   activeShopperOrders.push(order);
      //   ordersByShopper.set(order.activeShopperID, activeShopperOrders);
      // } else { //reassign orders to activeShopperID
      const assignedShopperOrders = ordersByShopper.get(shopper.shopperID) || [];
      assignedShopperOrders.push(order);
      ordersByShopper.set(shopper.shopperID, assignedShopperOrders);
      // }
    });
  });

  return mall.shoppers
    .map((shopper) => {
      const shopperOrders = ordersByShopper.get(shopper.shopperID) || [];

      if (!shopperOrders.length) {
        return {
          meta: {
            orderID: 'No orders',
            stores: [],
            shoppingStatus: 'No orders',
            badTiming: false,
          },
          x: earliestStoreStartTime,
          y: mall.shoppers.findIndex((s) => s.shopperID === shopper.shopperID) + 1,
          size: 0,
          customComponent: () => {
            return shopper.shopperStatus?.toLowerCase() === 'absent' ? (
              <g className="inner-component">
                <rect
                  x="0"
                  y="-15"
                  rx="3"
                  ry="3"
                  width={window.innerWidth * 0.85}
                  height={30}
                  fill={'#fff'}
                  stroke="#fff"
                />
                <rect
                  x="0"
                  y="0"
                  rx="3"
                  ry="3"
                  width={window.innerWidth * 0.85}
                  height={0.1}
                  fill="none"
                  stroke="#888888"
                  strokeWidth="1"
                  strokeDasharray="2,5"
                />
              </g>
            ) : null;
          },
        };
      }

      const sortedOrders = [...shopperOrders].sort((a, b) => {
        const aTime = moment(a.startTime, 'HH:mm');
        const bTime = moment(b.startTime, 'HH:mm');
        return aTime.diff(bTime);
      });

      const ordersWithTiming = sortedOrders.map((order, index) => {
        const orderWithTiming = { ...order, badTiming: false };
        const currentStartTime = moment(order.startTime, 'HH:mm:ss');
        const currentEndTime = moment(order.endTime, 'HH:mm:ss');

        if (index > 0) {
          const prevEndTime = moment(sortedOrders[index - 1].endTime, 'HH:mm:ss');
          const gapWithPrev = currentStartTime.diff(prevEndTime, 'minutes');
          if (gapWithPrev < 0) {
            orderWithTiming.badTiming = true;
          }
        }

        if (index < sortedOrders.length - 1) {
          const nextStartTime = moment(sortedOrders[index + 1].startTime, 'HH:mm:ss');
          const gapWithNext = nextStartTime.diff(currentEndTime, 'minutes');
          if (gapWithNext < 0) {
            orderWithTiming.badTiming = true;
          }
        }

        return orderWithTiming;
      });

      return ordersWithTiming.map((order) => {
        return {
          meta: {
            ...order,
            stores: order.stores.map((store) => {
              const mallStore = mall.stores.find((s) => s.storeID === store.storeID);
              return {
                ...store,
                imageURL: mallStore?.imageURL,
                name: mallStore?.name,
              };
            }),
          },
          x: parseInt(order.startTime.split(':')[0]) + parseInt(order.startTime.split(':')[1]) / 60,
          y: mall.shoppers.findIndex((s) => s.shopperID === shopper.shopperID) + 1,
          size: 33,
          customComponent: (row: MallRowItem) => {
            const order = row.meta;
            const activeShopper = mall.shoppers.find((s) => s.shopperID === order.activeShopperID) || shopper;
            const startHour = parseInt(order.startTime.split(':')[0]) + parseInt(order.startTime.split(':')[1]) / 60;
            const endHour = parseInt(order.endTime.split(':')[0]) + parseInt(order.endTime.split(':')[1]) / 60;
            const width = ((endHour - startHour) * (window.innerWidth * 0.85)) / 15;

            return (
              <g className="inner-inner-component">
                <rect
                  x="0"
                  y="-15"
                  rx="3"
                  ry="3"
                  width={width}
                  height={30}
                  dominantBaseline="middle"
                  fill={getColor(order, activeShopper)}
                  stroke={'#fff'}
                  strokeWidth={1}
                />
                <svg
                  width="16"
                  height="16"
                  display={'none'}
                  viewBox="0 0 16 16"
                  dominantBaseline="middle"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  x="8.5"
                  y="-8.5"
                >
                  <g clip-path="url(#clip0_1599_863)">
                    <path
                      d="M11.9998 4.00004H10.6665C10.6665 2.52671 9.47317 1.33337 7.99984 1.33337C6.5265 1.33337 5.33317 2.52671 5.33317 4.00004H3.99984C3.2665 4.00004 2.6665 4.60004 2.6665 5.33337V13.3334C2.6665 14.0667 3.2665 14.6667 3.99984 14.6667H11.9998C12.7332 14.6667 13.3332 14.0667 13.3332 13.3334V5.33337C13.3332 4.60004 12.7332 4.00004 11.9998 4.00004ZM7.99984 2.66671C8.73317 2.66671 9.33317 3.26671 9.33317 4.00004H6.6665C6.6665 3.26671 7.2665 2.66671 7.99984 2.66671ZM11.9998 13.3334H3.99984V5.33337H5.33317V6.66671C5.33317 7.03337 5.63317 7.33337 5.99984 7.33337C6.3665 7.33337 6.6665 7.03337 6.6665 6.66671V5.33337H9.33317V6.66671C9.33317 7.03337 9.63317 7.33337 9.99984 7.33337C10.3665 7.33337 10.6665 7.03337 10.6665 6.66671V5.33337H11.9998V13.3334Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1599_863">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </g>
            );
          },
        };
      });
    })
    .flat();
}

const Collapsible: React.FC<{ mall: Streams }> = ({ mall }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [hover, setHover] = useState<any>(null);
  const { push } = useHistory();

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest('.inner-inner-component') &&
        !target.closest('.cross') &&
        !target.closest('.MuiButton-root')
      ) {
        setHover(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getTotalOrders = (): number => {
    return mall.shoppers.reduce((total, shopper) => total + shopper.orders.length, 0);
  };

  const activeShoppers = mall.shoppers.filter((shopper) => shopper.shopperStatus?.toLowerCase() !== 'absent');

  const [earliestShopperShiftStartTime, latestShopperShiftEndTime] = mall.shoppers.reduce(
    ([earliest, latest], shopper) => [shopper.shifts[0].startTime, shopper.shifts[shopper.shifts.length - 1].endTime],
    ['00:00:00', '00:00:00']
  );

  const [earliestStoreStartTime, latestStoreEndTime] = mall.stores
    .map((store) => ({
      ...store,
      startTime: store.endTime === '00:00:00' ? '08:00:00' : store.startTime,
      endTime: store.endTime === '00:00:00' ? '17:00:00' : store.endTime,
    }))
    .reduce(
      ([earliest, latest], store) => [
        Math.min(earliest, parseInt(store.startTime.split(':')[0]) + parseInt(store.startTime.split(':')[1]) / 60 || 7),
        Math.max(latest, parseInt(store.endTime.split(':')[0]) + parseInt(store.endTime.split(':')[1]) / 60 || 22),
      ],
      [23, 0]
    );

  const openStores = mall.stores.reduce((total, store) => {
    const storeStartTime = moment(store.startTime, 'HH:mm');
    const storeEndTime = moment(store.endTime, 'HH:mm');
    const duration = moment.duration(storeEndTime.diff(storeStartTime));
    const hoursOpen = duration.asHours();

    return hoursOpen >= 1 ? total + 1 : total;
  }, 0);

  const xAxis = [
    earliestStoreStartTime,
    ...Array.from(
      { length: latestStoreEndTime - earliestStoreStartTime - 1 },
      (_, i) => Math.ceil(earliestStoreStartTime) + i
    ),
    latestStoreEndTime,
  ];

  const startingPoints = [
    {
      x: xAxis[0],
      y: 0,
      size: 1,
      style: { stroke: 'transparent', fill: 'transparent' },
    },
    {
      x: xAxis[xAxis.length - 1],
      y: 0,
      size: 1,
      style: { stroke: 'transparent', fill: 'transparent' },
    },
  ];

  const shopperCapacity = mall.indicators.availableShopperTime * 100;
  const data = generateAxesValues(mall, earliestStoreStartTime, latestStoreEndTime);
  const activeOrders = getTotalOrders();

  return (
    <Accordion
      TransitionProps={{ unmountOnExit: true }}
      expanded={expanded}
      onClick={(e) => {
        setExpanded(!expanded);
      }}
      classes={{
        expanded: classes.expanded,
        root: classes.expanded,
      }}
    >
      <AccordionSummary
        aria-label="Expand"
        expandIcon={expanded ? <RemoveIcon /> : <AddIcon />}
        aria-controls="additional-actions1-content"
        id="additional-actions1-header"
      >
        <FormControlLabel
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={<Checkbox color="primary" />}
          label=""
        />
        <div className={classes.header}>
          <div className={classes.mallTitleWrapper}>
            <Typography>{mall.mallName}</Typography>
            {(mall.indicators.missingTimings || mall.indicators.badTimings) && (
              <Typography className={classes.missingTiming} variant="caption">
                {mall.indicators.missingTimings ? 'Missing timing' : 'Bad timing'}
              </Typography>
            )}
          </div>
          <Chip
            label={`Scheduled hours: ${moment(earliestShopperShiftStartTime, 'HH:mm').format('hh:mm A')} - ${moment(
              latestShopperShiftEndTime,
              'HH:mm'
            ).format('hh:mm A')}`}
            style={{ backgroundColor: '#F7F7F7', borderRadius: '20px', fontSize: '12px', height: '22px' }}
          />
          <div style={{ width: '5rem' }}>
            <Chip
              label={`Stores: ${openStores} / ${mall.stores.length}`}
              style={{ backgroundColor: '#F7F7F7', borderRadius: '20px', fontSize: '12px', height: '22px' }}
            />
          </div>
          <div style={{ width: '7rem' }}>
            <Chip
              label={`Total orders: ${activeOrders}`}
              style={{ backgroundColor: '#F7F7F7', borderRadius: '20px', fontSize: '12px', height: '22px' }}
            />
          </div>
          <div style={{ width: '8rem' }}>
            <Chip
              label={`Missing shoppers: ${!!activeShoppers.length ? mall.indicators.missingShoppers : 'all'}`}
              style={{
                backgroundColor: mall.indicators.missingShoppers || !activeShoppers.length ? '#F7D8D8' : '#F7F7F7',
                color: mall.indicators.missingShoppers || !activeShoppers.length ? '#A50E0E' : '#000',
                borderRadius: '20px',
                fontSize: '12px',
                height: '22px',
              }}
            />
          </div>
          <div style={{ width: '12rem' }}>
            <Chip
              label={
                <>
                  <span
                    className={classes.capacityIndicator}
                    style={{ background: CAPACITY_STYLES[getCapacityLevel(shopperCapacity)].indicator }}
                  />
                  {`Shopping capacity: ${getCapacityLevel(shopperCapacity).toLowerCase()}`}
                </>
              }
              className={classes.capacityChip}
              style={{
                backgroundColor: CAPACITY_STYLES[getCapacityLevel(shopperCapacity)].background,
                color: CAPACITY_STYLES[getCapacityLevel(shopperCapacity)].color,
              }}
            />
          </div>
          <Chip
            label={`${mall.indicators.ordersRunningLate} orders running late`}
            style={{
              backgroundColor: mall.indicators.ordersRunningLate ? '#F7D8D8' : '#DDEDF5',
              color: mall.indicators.ordersRunningLate ? '#A50E0E' : '#000',
              borderRadius: '20px',
              fontSize: '12px',
              height: '22px',
            }}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails onClick={(event) => event.stopPropagation()}>
        <div>
          <XYPlot
            height={Math.max((mall.shoppers.length + mall.stores.length) * 50, window.innerHeight * 0.4)}
            width={window.innerWidth * 0.85}
            margin={{ left: 160, top: 20 }}
          >
            <XAxis
              tickFormat={(v) => (Number.isInteger(v) ? (v >= 12 ? `${v === 12 ? 12 : v - 12}pm` : `${v}am`) : '')}
              tickValues={[...xAxis, 17, 18, 19, 20, 21, 22]}
              on0={true}
              hideLine
              style={{
                text: { stroke: 'none', fill: '#000', fontWeight: 300 },
              }}
            />

            <LineSeries
              data={[
                { x: Math.max(7, xAxis[0]), y: 0 },
                { x: xAxis[xAxis.length - 1], y: 0 },
              ]}
              style={{
                stroke: '#75CCF2',
                strokeWidth: 1,
              }}
            />

            <LineSeries
              data={[
                { x: xAxis[xAxis.length - 1], y: 0 },
                { x: 22, y: 0 },
              ]}
              style={{
                stroke: '#CCBCBC',
                strokeWidth: 1,
                strokeDasharray: '4,4',
              }}
            />

            <YAxis
              tickValues={[
                ...mall.shoppers.map((_, index) => index + 1),
                ...mall.stores.map((_, index) => (index + 1) * -1),
              ]}
              hideLine
              tickFormat={(v) => {
                if (Number(v) > 0) {
                  const firstName = mall.shoppers[v - 1].name.split(' ')[0];
                  const surname = mall.shoppers[v - 1].surname;
                  const fullName = `${firstName} ${surname}`;

                  if (fullName.length > 18) {
                    const availableSpace = 18 - firstName.length - 4;
                    return `${firstName} ${surname.slice(0, availableSpace)}...`;
                  }
                  return fullName;
                }
                return '';
              }}
              style={{
                line: { stroke: '#fff' },
                text: {
                  stroke: 'none',
                  fill: '#000',
                  fontWeight: 300,
                  fontSize: 14,
                  textAnchor: 'end',
                  alignmentBaseline: 'middle',
                },
              }}
            />

            {mall.shoppers.map(
              (
                shopper: {
                  shifts: { startTime: string; endTime: string }[];
                  shopperStatus?: string;
                  orders: ShopperOrder[];
                },
                index: number
              ) => {
                const isAbsentWithOrders = shopper.shopperStatus?.toLowerCase() === 'absent';
                return shopper.shifts.map((shift: { startTime: string; endTime: string }, shiftIndex: number) => {
                  const startHour =
                    parseInt(shift.startTime.split(':')[0]) + parseInt(shift.startTime.split(':')[1]) / 60;
                  const endHour = parseInt(shift.endTime.split(':')[0]) + parseInt(shift.endTime.split(':')[1]) / 60;
                  return (
                    <LineSeries
                      key={`shift-${index}-${shiftIndex}`}
                      data={[
                        { x: startHour, y: index + 1 },
                        { x: endHour, y: index + 1 },
                      ]}
                      style={{
                        stroke: isAbsentWithOrders ? '#999999' : '#000',
                        strokeWidth: 1,
                        strokeDasharray: isAbsentWithOrders ? '4,4' : 'none',
                      }}
                    />
                  );
                });
              }
            )}

            <CustomSVGSeries
              data={mall.stores.map((store, index) => ({
                x: 7,
                y: (index + 1) * -1,
                size: 1,
                customComponent: () => (
                  <g transform={`translate(-150, -10)`}>
                    {store.imageURL && (
                      <image
                        href={store.imageURL}
                        width="20"
                        height="20"
                        x="0"
                        y="0"
                        preserveAspectRatio="xMidYMid meet"
                      />
                    )}
                    <text
                      x="25"
                      y="14"
                      style={{
                        fontSize: 14,
                        fontWeight: 300,
                        fill: store.startTime === '00:00:00' ? '#A50E0E' : '#000',
                      }}
                    >
                      {store.name}
                    </text>
                  </g>
                ),
              }))}
            />

            {mall.stores.map((store: { startTime: string; endTime: string }, index: number) => (
              <LineSeries
                key={`store-${index}`}
                data={[
                  {
                    x:
                      store.startTime === '00:00:00'
                        ? earliestStoreStartTime
                        : parseInt(store.startTime.split(':')[0]) + parseInt(store.startTime.split(':')[1]) / 60,
                    y: (index + 1) * -1,
                  },
                  {
                    x:
                      store.endTime === '00:00:00'
                        ? latestStoreEndTime
                        : parseInt(store.endTime.split(':')[0]) + parseInt(store.endTime.split(':')[1]) / 60,
                    y: (index + 1) * -1,
                  },
                ]}
                style={{
                  stroke: store.startTime === '00:00:00' ? '#888888' : '#000',
                  strokeDasharray: store.startTime === '00:00:00' ? '2,2' : 'none',
                  strokeWidth: 1,
                }}
              />
            ))}

            {!!data.length && (
              <CustomSVGSeries
                customComponent="square"
                onValueMouseOver={(value) => setHover(value)}
                data={[...data, ...startingPoints]}
              />
            )}
            {!!hover?.size && !!hover?.meta && (
              <Hint value={hover} style={{ fontSize: 14 }}>
                <div className={classes.cross} onMouseLeave={() => setHover(null)}>
                  {hover.meta?.stores?.map((store: any) => {
                    return (
                      <div className={classes.crossInner} key={store.name}>
                        <div key={store?.name} className={classes.crossImgWrapper}>
                          <img className={classes.crossImg} src={store.imageURL} alt={store.name} />
                          <Typography variant="body1" className={classes.bolder}>
                            {store?.name}
                          </Typography>
                          <Typography variant="body1" className={classes.crossText}>
                            {store?.quantity} items
                          </Typography>
                        </div>
                      </div>
                    );
                  })}
                  <div className={classes.footerText}>
                    <Typography variant="body1" className={classes.bolder}>
                      Delivery Slot
                    </Typography>
                    <Typography variant="body1" className={classes.crossText}>
                      {moment(hover?.meta?.slotStartTime, 'HH:mm').format('ha')} -{' '}
                      {moment(hover?.meta?.slotEndTime, 'HH:mm').format('ha')}
                    </Typography>
                  </div>
                  <div className={classes.footerText}>
                    <Typography variant="body1" className={classes.bolder}>
                      Order Number
                    </Typography>
                    <Typography variant="body1" className={classes.crossText}>
                      {hover.meta.orderID}
                    </Typography>
                  </div>
                  <div className={classes.footerText}>
                    <Typography variant="body1" className={classes.bolder}>
                      Scheduled time
                    </Typography>
                    <Typography variant="body1" className={classes.crossText}>
                      {moment(hover.meta.startTime, 'HH:mm:ss').format('hh:mm A')} -{' '}
                      {moment(hover.meta.endTime, 'HH:mm:ss').format('hh:mm A')}
                    </Typography>
                  </div>
                  {!!hover.meta.activeShopperID && hover.meta.shopperStartTime && (
                    <Fragment>
                      <div className={classes.footerText}>
                        <Typography variant="body1" className={classes.bolder}>
                          Shopped time
                        </Typography>
                        <Typography variant="body1" className={classes.crossText}>
                          {moment(hover.meta.shopperStartTime, 'YYYY-MM-DD HH:mm:ss').format('hh:mm A')} -{' '}
                          {hover.meta.shopperEndTime
                            ? moment(hover.meta.shopperEndTime, 'YYYY-MM-DD HH:mm:ss').format('hh:mm A')
                            : 'N/A'}
                        </Typography>
                      </div>
                      <div className={classes.footerText}>
                        <Typography variant="body1" className={classes.bolder}>
                          Shopped by
                        </Typography>
                        <Typography variant="body1" className={classes.crossText}>
                          {mall.shoppers.find((s) => s.shopperID === hover.meta.activeShopperID)?.name}
                        </Typography>
                      </div>
                    </Fragment>
                  )}
                  <Button
                    variant="text"
                    color="primary"
                    className={classes.viewDetails}
                    onClick={() => {
                      push(`/orders/${hover.meta.orderID}`);
                    }}
                  >
                    View order details
                  </Button>
                </div>
              </Hint>
            )}
          </XYPlot>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

interface Props {
  mallItems: Streams[];
}

const legends = [
  {
    indicator: '#75CCF2',
    label: 'Mall Operating Time',
  },
  {
    indicator: '#5BB980',
    label: 'Order In Progress',
  },
  {
    indicator: '#263B84',
    label: 'Scheduled Orders',
  },
  {
    indicator: '#B9EC29',
    label: 'Shopper Schedule',
  },
  {
    indicator: '#888',
    label: 'Shopper Absent',
  },
  {
    indicator: '#A50E0E',
    label: 'Shopper Running Late',
  },
];

const MallItems: React.FC<Props> = ({ mallItems }) => {
  const classes = useStyles();
  return (
    <Grid container direction="column" className={classes.root}>
      <div className={classes.legend}>
        {legends.map((legend) => (
          <Typography className={classes.legendText} key={legend.label}>
            <span className={classes.indicator} style={{ background: legend.indicator }} />
            {legend.label}
          </Typography>
        ))}
      </div>
      {mallItems?.map((mall) => (
        <Collapsible key={mall.mallID} mall={mall} />
      ))}
    </Grid>
  );
};

export default MallItems;
